import {Component, Input, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {NgIf} from "@angular/common";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ToastrService} from "ngx-toastr";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {OtpModeService} from "@shared/api/services/v2/otp-mode.service";
import {OtpModeEnum} from "@shared/api/models/query/otp-mode-history.model";

/**
 * UNIVAPLAT-2992: admin-dashboard modal that turns a user's OTP delivery
 * mode to `static` — pinning a fixed 4-digit code, with a required reason —
 * via `POST /api/v2/admin/users/{userId}/otp-mode/static`
 * ({@link OtpModeService.setStatic}, backend contract UNIVAPLAT-2977).
 *
 * Opened from the admin-dashboard row kebab menu's "Turn to static" item
 * (UNIVAPLAT-2991 stub, {@link AdminDashboardPage.openTurnToStatic}) for a
 * row whose OTP mode is not already static. Modelled on
 * {@link LicenseDeactivateModal}'s form/validation/error-handling shape.
 *
 * Client-side validation (fast feedback only — the backend re-validates
 * everything and its rejection wins, per acceptance criteria):
 * - code: required, exactly 4 digits (`codeFormat`)
 * - code: not a sequential run (ascending or descending, e.g. `1234`,
 *   `4321`, `0123`, `9876`) and not all four digits identical (e.g. `0000`)
 *   (`codeWeak`)
 * - code: not the code currently in force for this user, when the user's
 *   OTP history carries one — resolved on init from
 *   {@link OtpModeService.listHistory} (`codeInForce`). In the common case
 *   (switching from dynamic, the only mode this modal opens for) there is no
 *   current static code and this check is a no-op; it is defensive against
 *   the user's most recent history entry still being `static` (e.g. a stale
 *   row).
 * - reason: required after trimming (`required`) — mirrors
 *   {@link LicenseDeactivateModal}'s `trimmedRequired` pattern.
 *
 * Backend errors (403, 409, 422) surface verbatim via
 * `toastr.error(error.error.error)` — backend owns all user-facing error
 * text — and the modal stays open with the admin's input intact. Success:
 * toastr.success + `modal.close(true)` so the caller's
 * `ref.result.then(...)` can refresh the row
 * ({@link AdminDashboardPage.onOtpModeSwitched}).
 *
 * Security constraint (tech-lead assessment): the code and reason are
 * PHI-adjacent — never logged (console, analytics, or any logging call,
 * Sentry breadcrumbs included).
 *
 * UNIVAPLAT-3027: leading-zero codes (e.g. `0234`) audited end-to-end —
 * the `code` control is declared `FormControl<string>`, `codeFormatValidator`
 * and `codeWeakValidator` operate on the raw string (no `Number()` /
 * `parseInt()` anywhere in this file), the `<input>` is `type="text"`
 * (never `type="number"`, which would silently strip a leading zero as the
 * admin types), and {@link OtpModeService.setStatic} forwards the trimmed
 * string unchanged in a `SetStaticOtpMode` body typed `code: string`. This
 * was already true before this ticket — confirmed, not changed — now that
 * the backend (UNIVAPLAT-3026) stores `otp_static_code` as a string rather
 * than coercing it to a number.
 */

const CODE_PATTERN = /^\d{4}$/;

/**
 * `Validators.required` accepts whitespace-only strings, but `submit()` trims
 * the value — so "   " would slip past the client-side gate and POST an
 * empty reason. Reject any value that trims to '' with the same `required`
 * error key so a single template error message covers both cases.
 */
const trimmedRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim() === '' ? {required: true} : null;

/** Exactly 4 characters, digits only. */
const codeFormatValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    CODE_PATTERN.test(control.value ?? '') ? null : {codeFormat: true};

/**
 * Rejects a fully sequential 4-digit run (ascending or descending, e.g.
 * `1234`, `4321`, `0123`, `9876`) or four identical digits (e.g. `0000`).
 * Only meaningful once the value is already 4 digits — a value that fails
 * that shape is left to {@link codeFormatValidator} rather than double
 * reporting here.
 */
const codeWeakValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? '';
    if (!CODE_PATTERN.test(value)) {
        return null;
    }
    const digits = value.split('').map(Number);
    const allIdentical = digits.every(d => d === digits[0]);
    const diffs = digits.slice(1).map((d, i) => d - digits[i]);
    const isSequential = diffs.every(d => d === 1) || diffs.every(d => d === -1);
    return (allIdentical || isSequential) ? {codeWeak: true} : null;
};

@Component({
    selector: 'cdss-otp-mode-static-modal',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgIf,
        SpinnerComponent
    ],
    templateUrl: './otp-mode-static.modal.html'
})
export class OtpModeStaticModal implements OnInit {

    @Input() userId: number;
    /** Display-only context in the modal header; may arrive masked (PHI masking). */
    @Input() userName = '';

    loading = false;
    saving = false;

    /**
     * Resolved on init from the user's OTP mode history: the code presently
     * in force, if the row carries one (see class-level doc). Never
     * rendered or logged — used only by {@link codeInForceValidator}.
     */
    private currentCode: string | null = null;

    /**
     * Bound as an instance property (rather than a top-level ValidatorFn) so
     * it can read {@link currentCode} once the async history lookup in
     * {@link ngOnInit} resolves.
     */
    private codeInForceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
        (this.currentCode !== null && control.value === this.currentCode) ? {codeInForce: true} : null;

    form = new FormGroup({
        code: new FormControl<string>('', [
            Validators.required,
            codeFormatValidator,
            codeWeakValidator,
            (control: AbstractControl) => this.codeInForceValidator(control)
        ]),
        reason: new FormControl<string>('', [trimmedRequired]),
    });

    constructor(
        public modal: NgbActiveModal,
        private otpModeService: OtpModeService,
        private toastr: ToastrService,
    ) {
    }

    async ngOnInit() {
        this.loading = true;
        try {
            const history = await this.otpModeService.listHistory(this.userId);
            // Newest-first: if the latest change set the mode to static, that
            // entry's code is the one currently in force.
            const latest = history[0];
            if (latest && latest.newMode === OtpModeEnum.STATIC) {
                this.currentCode = latest.staticCode;
                this.form.controls.code.updateValueAndValidity();
            }
        } catch (error: any) {
            // Backend owns the message (e.g. 404 unknown user) — surface verbatim.
            this.toastr.error(error?.error?.error);
            this.modal.close(false);
        } finally {
            this.loading = false;
        }
    }

    async submit() {
        if (this.saving || this.loading) {
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const code = (this.form.value.code ?? '').trim();
        const reason = (this.form.value.reason ?? '').trim();
        this.saving = true;
        try {
            await this.otpModeService.setStatic(this.userId, code, reason);
            this.toastr.success('OTP mode switched to static', 'Success');
            this.modal.close(true);
        } catch (error: any) {
            // Backend owns the user-facing error text (403, 409, 422) — no
            // hardcoded frontend copy. The modal stays open, with the
            // admin's input intact, so they can correct and retry.
            this.toastr.error(error?.error?.error);
        } finally {
            this.saving = false;
        }
    }
}
